/** @file drawable.set: merges fields of the drawable's own kind into it. */
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { withPathPrefix } from '../validate/withPathPrefix';
import { readInput } from '../validate/readInput';
import { fieldsShapeOf } from './fieldsShapeOf';
import { findDrawable } from './findDrawable';
import { pivotWarnings } from './pivotWarnings';

/**
 * `drawable.set { drawable, fields }` → `{}`: fields must belong to the drawable's kind (`VALIDATION` otherwise);
 * warns `PIVOT_OUTSIDE`. Inverse: `drawable.set` with the previous values when every field existed, else P.
 */
export const drawableSetCommand: CommandDef<'drawable.set'> = {
  name: 'drawable.set',
  /** Checks the raw input (VALIDATION under the input path); the fields are checked against the kind in apply. */
  validate: (input, path) => {
    const r = readInput(input, path, ['drawable', 'fields']);
    return { drawable: r.str('drawable'), fields: r.rec('fields') };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const { index, drawable } = findDrawable(draft.puppet, input.drawable);
    withPathPrefix('/input/fields', () => expectShape(input.fields, '', fieldsShapeOf(drawable.kind)));
    const target = drawable as unknown as Record<string, unknown>;
    const keys = Object.keys(input.fields);
    const inverse = keys.every((k) => target[k] !== undefined)
      ? [{ name: 'drawable.set' as const, input: { drawable: input.drawable, fields: structuredClone(Object.fromEntries(keys.map((k) => [k, target[k]]))) } }]
      : puppetInverse(draft);
    Object.assign(target, structuredClone(input.fields));
    checkPuppet(draft, ctx);
    return { result: {}, warnings: pivotWarnings(drawable, index), inverse };
  },
};
